import { useEffect } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AUTH_REDIRECT_KEY, HTTP_STATUS } from '@/constants';
import { meQueryOptions, useCurrentUser, type CurrentUser } from '@/features/auth/useCurrentUser';
import { HttpError, setFetchClientTokenGetter } from '@/lib/fetchClient';
import { LoadingScreen } from '@/features/layout/LoadingScreen';
import { AdminLayout } from '@/features/admin/AdminLayout';

export const Route = createFileRoute('/admin')({
  beforeLoad: ({ context, location }) => {
    setFetchClientTokenGetter(context.auth.getAccessToken);

    if (!context.auth.isLoading && !context.auth.isAuthenticated) {
      const path = location.pathname;
      if (path !== '/' && !path.startsWith('/sign-in')) {
        sessionStorage.setItem(AUTH_REDIRECT_KEY, location.href);
      }
      throw redirect({ to: '/sign-in' });
    }
  },

  loader: async ({ context }) => {
    if (!context.auth.isAuthenticated) return;

    let me: CurrentUser;
    try {
      me = await context.queryClient.ensureQueryData(meQueryOptions());
    } catch (error) {
      if (error instanceof HttpError) {
        if (error.response.status === HTTP_STATUS.UNAUTHORIZED) {
          throw redirect({ to: '/sign-in' });
        }
        if (error.response.status === HTTP_STATUS.FORBIDDEN) {
          throw redirect({ to: '/403' });
        }
        if (error.response.status >= HTTP_STATUS.SERVER_ERROR) {
          throw redirect({ to: '/500' });
        }
      }
      throw redirect({ to: '/500' });
    }

    if (!me.isAdmin) {
      throw redirect({ to: '/403' });
    }
  },

  component: AdminRoot,
});

// Component-level guard duplicates the loader's isAdmin check on purpose:
// when MSAL is still initialising at navigation time the loader sees
// `isAuthenticated: false` and early-returns without probing /api/me, and
// TanStack Router caches that result. Without this reactive guard a non-admin
// could land on AdminLayout before the loader ever runs the check.
function AdminRoot() {
  const { auth } = Route.useRouteContext();
  const navigate = useNavigate();
  const { data: me, isLoading: meLoading } = useCurrentUser({
    enabled: auth.isAuthenticated,
  });

  useEffect(() => {
    setFetchClientTokenGetter(auth.getAccessToken);
  }, [auth.getAccessToken]);

  useEffect(() => {
    if (!auth.isLoading && !auth.isAuthenticated) {
      const path = window.location.pathname;
      if (path !== '/' && !path.startsWith('/sign-in')) {
        sessionStorage.setItem(
          AUTH_REDIRECT_KEY,
          window.location.pathname + window.location.search + window.location.hash
        );
      }
      navigate({ to: '/sign-in', replace: true });
    }
  }, [auth.isLoading, auth.isAuthenticated, navigate]);

  useEffect(() => {
    if (me && !me.isAdmin) {
      navigate({ to: '/403', replace: true });
    }
  }, [me, navigate]);

  if (auth.isLoading || meLoading || !me || !me.isAdmin) {
    return <LoadingScreen message='Loading...' />;
  }

  return <AdminLayout />;
}
